/**
 * Header Component - Dark Navbar Theme
 * 
 * IMPORTANT: This navbar uses a PERMANENT dark burgundy background (#6f1617)
 * with white text and gold (#FFD700) accents for active/hover states.
 * This ensures the logo is always visible.
 */
import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Modal } from './shared/Modal';
import { restaurantConfig } from '../config/restaurantConfig';
import AnimatedHamburger from './AnimatedHamburger';
import DrawerNuclear from './DrawerNuclear';
import { FaCalendarAlt, FaChevronDown } from 'react-icons/fa';

// Import flag images
import ptFlagSvg from '../assets/images/flags/pt.svg';
import ukFlagSvg from '../assets/images/flags/uk.svg';

// Import logos
import logo from '../assets/images/logoNavbar.webp';
import logoWhite from '../assets/images/logoWhite.webp';
import tagusLogo from '../assets/images/tagus.png';

interface HeaderProps {
  language: string;
  toggleLanguage: () => void;
  handleLinkClick?: () => void;
  isMobileMenuOpen?: boolean;
  toggleMobileMenu?: () => void;
  isScrolled?: boolean;
}

// Get logo size from config
const logoSizeScale = restaurantConfig.features?.logoSize?.navbar || 1;

export const Header = ({ language, toggleLanguage, handleLinkClick = () => { } }: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [reservationModalIsOpen, setReservationModalIsOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const logoRef = useRef<HTMLImageElement>(null);
  const [logoVisible, setLogoVisible] = useState(false);
  const [mobileLogoVisible, setMobileLogoVisible] = useState(false);
  const [animationActive, setAnimationActive] = useState(false);
  const [heroLogoAnimationComplete, setHeroLogoAnimationComplete] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Add section observer to highlight active section in navbar
  useEffect(() => {
    if (isHomePage) {
      // List all sections in order of appearance on the page
      const sectionIds = ['home', 'menu', 'about', 'services', 'gallery', 'testimonials', 'fidelity', 'map', 'contact'];

      // More reliable detection settings - top-biased
      const observerOptions = {
        root: null,
        rootMargin: '-5% 0px -75% 0px', // When 25% of section is visible at the top
        threshold: 0
      };

      const observerCallback = (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, observerOptions);

      // First try to observe the div wrappers for each section
      sectionIds.forEach(id => {
        const element = document.getElementById(id);
        if (element) {
          observer.observe(element);
        } else {
          // If wrapper not found, try to find a section with the same ID
          const sectionElement = document.querySelector(`section#${id}`);
          if (sectionElement) {
            observer.observe(sectionElement);
          } else {
            console.warn(`Section with ID ${id} not found`);
          }
        }
      });

      // Force "home" section as active when at the top of the page
      if (window.scrollY < 100) {
        setActiveSection('home');
      }

      // Improved scroll event handler with throttling
      let lastScrollTime = 0;
      const handleScroll = () => {
        const now = Date.now();
        if (now - lastScrollTime < 100) return; // Throttle to every 100ms
        lastScrollTime = now;

        if (window.scrollY < 100) {
          setActiveSection('home');
        } else {
          // Detect which section is most in view
          let maxVisibleSection = '';
          let maxVisibleAmount = 0;

          sectionIds.forEach(id => {
            const element = document.getElementById(id) || document.querySelector(`section#${id}`);
            if (element) {
              const rect = element.getBoundingClientRect();
              const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
              if (visibleHeight > maxVisibleAmount && visibleHeight > 0) {
                maxVisibleAmount = visibleHeight;
                maxVisibleSection = id;
              }
            }
          });

          if (maxVisibleSection && maxVisibleAmount > 100) { // At least 100px visible
            setActiveSection(maxVisibleSection);
          }
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        sectionIds.forEach(id => {
          const element = document.getElementById(id);
          if (element) observer.unobserve(element);
          const sectionElement = document.querySelector(`section#${id}`);
          if (sectionElement) observer.unobserve(sectionElement);
        });
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isHomePage]);

  // Effect to handle hero logo animation
  useEffect(() => {
    if (isHomePage) {
      // Disable hero logo animation on scroll - fix for logo disappearing
      const handleHeroLogoAnimation = () => {
        // No animation changes to hero logo when scrolling
        // Just handle mobile logo visibility
        if (window.scrollY > 50) {
          setMobileLogoVisible(true);
        } else {
          setMobileLogoVisible(false);
        }
      };

      window.addEventListener('scroll', handleHeroLogoAnimation);
      return () => {
        window.removeEventListener('scroll', handleHeroLogoAnimation);
      };
    } else {
      // On non-home pages, always show the mobile logo
      setMobileLogoVisible(true);
    }
  }, [isHomePage]);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;

      // Use requestAnimationFrame to improve scroll performance
      requestAnimationFrame(() => {
        if (offset > 10) {
          if (!scrolled) {
            setScrolled(true);
          }
        } else {
          if (scrolled) {
            if (isHomePage) {
              setLogoVisible(false);
              setScrolled(false);
            } else {
              setScrolled(false);
            }
          }
        }
      });
    };

    // Use passive event listener to improve scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Set initial logo visibility - always false on homepage
    const initialScrollPosition = window.scrollY > 10;
    setLogoVisible(!isHomePage ? initialScrollPosition : false);
    setScrolled(initialScrollPosition);

    // Set initial mobile logo visibility
    if (!isHomePage) {
      setMobileLogoVisible(true);
    } else {
      setMobileLogoVisible(initialScrollPosition);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage, scrolled, logoVisible]);

  const toggleMobileMenu = () => {
    setMobileMenu(!mobileMenu);
  };

  // Function to handle all section scrolling
  const scrollToSection = useCallback((sectionId: string, e?: React.MouseEvent) => {
    if (e) e.preventDefault();

    // Close menu if it's open
    if (mobileMenu) {
      setMobileMenu(false);
    }

    // Check if on homepage
    if (location.pathname !== '/') {
      // Navigate to homepage first, then scroll to section
      window.location.href = `/#${sectionId}`;
      return;
    }

    // On homepage, just scroll to section
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      // Set active section manually to update UI immediately
      setActiveSection(sectionId);
    }
  }, [mobileMenu, location.pathname]);

  // Previous functions can now use the common scrollToSection function
  const scrollToBookingSection = useCallback((e?: React.MouseEvent) => {
    scrollToSection('booking', e);
  }, [scrollToSection]);

  // Smooth scroll to contact section
  const scrollToContact = useCallback((e: React.MouseEvent) => {
    scrollToSection('contact', e);
  }, [scrollToSection]);

  // Handle link click and close menu
  const handleNavLinkClick = useCallback((sectionId: string, e?: React.MouseEvent) => {
    // Prevent default if event is provided
    if (e) e.preventDefault();

    // Close the menu
    setMobileMenu(false);

    // Scroll to section
    scrollToSection(sectionId, e);

    // Execute the parent handler function
    handleLinkClick();
  }, [handleLinkClick, scrollToSection]);

  // For non-homepage, show the logo; for homepage, always hide it
  const shouldShowLogo = !isHomePage && logoVisible;

  const headerStyle = {
    backgroundColor: '#6f1617',
    color: 'white',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    transition: 'background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease',
  };

  const navLinkStyle = {
    color: 'white',
    textShadow: 'none',
    fontWeight: 700,
    transition: 'color 0.3s ease, text-shadow 0.3s ease',
  };

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 main-header py-3`} style={{
      backgroundColor: '#6f1617',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
    }}>
      {/* Custom CSS to enforce styling */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* NUCLEAR: Hide mobile-only navbar elements on desktop */
          @media (min-width: 1024px) {
            .mobile-hamburger {
              display: none !important;
            }
            /* Hide the entire mobile navbar container on desktop */
            .mobile-navbar-container {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              pointer-events: none !important;
            }
          }
          
          /* Override header background - always dark */
          header, header.main-header {
            background-color: #6f1617 !important;
          }
          
          /* Custom container size */
          .container {
            max-width: 2000px !important;
            margin-left: auto;
            margin-right: auto;
            padding-left: var(--spacing-lg, 1rem);
            padding-right: var(--spacing-lg, 1rem);
          }
          
          /* Style for all navigation items - always white text */
          .navigation li a {
            position: relative;
            transition: color 0.3s ease;
            padding: 8px 0;
            color: white !important;
          }
          
          /* Hover effect - golden color */
          .navigation li a:hover {
            color: #FFD700 !important;
          }
          
          .navigation li a::after {
            content: '';
            position: absolute;
            bottom: -4px;
            left: 0;
            width: 100%;
            height: 4px;
            background-color: #FFD700;
            transform: scaleX(0);
            transform-origin: center;
            transition: transform 0.3s ease;
          }
          
          .navigation li a:hover::after {
            transform: scaleX(0.5);
          }
          
          /* Style for active navigation item */
          .navigation li.active a {
            color: #FFD700 !important;
            font-weight: 800 !important;
            position: relative;
          }
          
          /* Add dot indicator before active item */
          .navigation li.active a::before {
            content: '•';
            position: absolute;
            top: 50%;
            left: -15px;
            transform: translateY(-50%);
            color: #FFD700;
            font-size: 24px;
          }
          
          .navigation li.active a::after {
            transform: scaleX(1);
            background-color: #FFD700;
            height: 4px;
            box-shadow: 0 0 8px rgba(255, 215, 0, 0.6);
          }
          
          /* Make nav text actually bold */
          .nav-link {
            font-weight: 700 !important;
          }
          
          /* New navbar structure: 3-column layout */
          .navbar-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            height: 100%;
          }
          
          /* Left section: logo - on far left for desktop */
          .navbar-left {
            flex: 0 0 20%;
            display: flex;
            justify-content: flex-start;
            align-items: center;
          }
          
          /* Center section: navigation only */
          .navbar-center {
            flex: 0 0 60%;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
          }

          /* Right section: book now and languages */
          .navbar-right {
            flex: 0 0 20%;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 1rem;
          }
          
          /* Mobile styles for navbar sections - NUCLEAR */
          @media (max-width: 1023px) {
            .navbar-center, .navbar-right {
              display: none !important;
              visibility: hidden !important;
            }
            
            .container.mx-auto {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            
            .navbar-wrapper {
              display: block !important;
              position: relative !important;
              min-height: 60px !important;
              width: 100% !important;
            }
            
            /* Logo dead center on mobile - relative to full viewport */
            .navbar-left {
              position: fixed !important;
              left: 50vw !important;
              top: 30px !important;
              transform: translateX(-50%) !important;
              flex: none !important;
              width: auto !important;
              z-index: 9998 !important;
            }
            
            /* Hamburger far right on mobile */
            .mobile-hamburger {
              position: absolute !important;
              right: 16px !important;
              top: 50% !important;
              transform: translateY(-50%) !important;
              display: flex !important;
              z-index: 9999 !important;
            }
          }
          
          /* Center the navigation items */
          .navigation {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 2.5rem;
          }
          
          /* Book now button style */
          .book-now-btn {
            background-color: #6f1617;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 9999px;
            font-weight: bold;
            border: 2px solid transparent;
            transition: all 0.3s ease;
            box-shadow: 0 4px 6px rgba(17, 15, 2, 0.1);
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.9rem;
          }

          .book-now-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 10px rgba(17, 15, 2, 0.15);
            background-color: var(--color-primary-light);
            border-color: white;
          }
          
          /* Language dropdown */
          .language-dropdown {
            position: relative;
          }
          
          .language-dropdown-btn {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .language-dropdown-btn:hover {
            transform: translateY(-2px);
          }
          
          .language-dropdown-content {
            position: absolute;
            top: 100%;
            right: 0;
            background-color: white;
            border-radius: 0.5rem;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
            padding: 0.5rem;
            margin-top: 0.5rem;
            z-index: 100;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          
          /* Navbar logo - glow pulse animation */
          .navbar-logo {
            transition: all 0.3s ease;
            animation: navLogoGlow 4s ease-in-out infinite;
          }
          
          .navbar-logo:hover {
            transform: scale(${logoSizeScale * 1.05});
          }
          
          @keyframes navLogoGlow {
            0% { filter: drop-shadow(0 0 3px rgba(243, 231, 155, 0.2)); }
            50% { filter: drop-shadow(0 0 8px rgba(243, 231, 155, 0.4)) drop-shadow(0 0 12px rgba(212, 175, 55, 0.25)); }
            100% { filter: drop-shadow(0 0 3px rgba(243, 231, 155, 0.2)); }
          }
        `
      }} />

      {/* Mobile navbar - logo centered, hamburger right */}
      <div
        className="lg:hidden mobile-navbar-container"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
      >
        {/* Centered logo */}
        <Link
          to="/"
          onClick={() => {
            if (location.pathname === '/') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        >
          <img
            src={logo}
            alt={`${restaurantConfig.name} Logo`}
            className="navbar-logo"
            style={{
              height: '56px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Link>

        {/* Hamburger far right */}
        <button
          onClick={toggleMobileMenu}
          style={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '40px',
            height: '40px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer'
          }}
          aria-label={mobileMenu ? 'Close menu' : 'Open menu'}
        >
          <span className={`block w-7 h-0.5 rounded-full transition-all duration-300 mb-1.5 ${mobileMenu ? 'transform rotate-45 translate-y-2' : ''} bg-white`} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}></span>
          <span className={`block w-7 h-0.5 rounded-full transition-all duration-300 ${mobileMenu ? 'opacity-0' : 'opacity-100'} bg-white`} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}></span>
          <span className={`block w-7 h-0.5 rounded-full transition-all duration-300 mt-1.5 ${mobileMenu ? 'transform -rotate-45 -translate-y-2' : ''} bg-white`} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}></span>
        </button>
      </div>

      {/* Main navbar container */}
      <div className="container mx-auto px-4 h-full" style={{ position: 'relative' }}>
        <div className="navbar-wrapper h-full" style={{ position: 'relative', minHeight: '60px' }}>
          {/* Left section: Logo - DESKTOP ONLY */}
          <div
            className="navbar-left hidden lg:flex"
            style={{
              display: 'none', // Default to hidden
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* This inner div will be overridden by flex in desktop media query */}
            <style dangerouslySetInnerHTML={{
              __html: `
                @media (min-width: 1024px) {
                  .navbar-left {
                    display: flex !important;
                    justify-content: flex-start !important;
                  }
                }
              `
            }} />
            <Link
              to="/"
              className="logo-link block relative"
              onClick={() => {
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <img
                src={logo}
                alt={`${restaurantConfig.name} Logo`}
                className="h-16 w-auto object-contain transition-all duration-300 navbar-logo"
                ref={logoRef}
                style={{
                  transform: `scale(${logoSizeScale})`,
                  transformOrigin: 'center center',
                  maxHeight: '64px'
                }}
              />
            </Link>
          </div>

          {/* Center section: Navigation only */}
          <div className="navbar-center hidden lg:flex">
            <nav className="main-menu h-full flex items-center">
              <ul className="navigation">
                <li className={`${location.pathname === '/' && activeSection === 'home' ? 'active' : ''}`}>
                  <a
                    href="/"
                    className="nav-link"
                    onClick={(e) => {
                      e.preventDefault();
                      if (location.pathname !== '/') {
                        window.location.href = '/';
                      } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setActiveSection('home');
                      }
                    }}
                  >
                    {language === 'en' ? 'Home' : 'Início'}
                  </a>
                </li>

                <li className={`${activeSection === 'about' ? 'active' : ''}`}>
                  <a
                    href="#about"
                    className="nav-link"
                    onClick={(e) => handleNavLinkClick('about', e)}
                  >
                    {language === 'en' ? 'About Us' : 'Sobre Nós'}
                  </a>
                </li>


                <li className={`${location.pathname === '/menu-full' ? 'active' : ''}`}>
                  <Link
                    to="/menu-full"
                    className="nav-link"
                    onClick={() => {
                      setMobileMenu(false);
                    }}
                  >
                    Menu
                  </Link>
                </li>

                <li className={`${activeSection === 'gallery' ? 'active' : ''}`}>
                  <a
                    href="#gallery"
                    className="nav-link"
                    onClick={(e) => handleNavLinkClick('gallery', e)}
                  >
                    {language === 'en' ? 'Gallery' : 'Galeria'}
                  </a>
                </li>

                <li className={`${activeSection === 'contact' ? 'active' : ''}`}>
                  <a
                    href="#contact"
                    className="nav-link"
                    onClick={(e) => handleNavLinkClick('contact', e)}
                  >
                    {language === 'en' ? 'Contact' : 'Contacto'}
                  </a>
                </li>


              </ul>
            </nav>
          </div>

          {/* Right section: Book Now and Language dropdown */}
          <div className="navbar-right">
            {/* Language dropdown */}
            <div className="language-dropdown">
              <div
                className="language-dropdown-btn"
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                <img
                  src={language === 'en' ? ukFlagSvg : ptFlagSvg}
                  alt={language === 'en' ? 'English' : 'Português'}
                  className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                />
                <FaChevronDown className={`text-xs transition-transform duration-300 ${showLanguageDropdown ? 'rotate-180' : ''} text-white`} />
              </div>

              {showLanguageDropdown && (
                <div className="language-dropdown-content">
                  <button
                    onClick={() => {
                      localStorage.setItem('language', 'en');
                      toggleLanguage();
                      setShowLanguageDropdown(false);
                    }}
                    className={`w-8 h-8 rounded-full overflow-hidden transition-transform duration-300 hover:scale-110 ${language === 'en' ? 'border-2 border-[var(--color-primary)]' : 'border border-gray-300'}`}
                    title="English"
                  >
                    <img src={ukFlagSvg} alt="English" className="w-full h-full object-cover" />
                  </button>
                  <button
                    onClick={() => {
                      localStorage.setItem('language', 'pt');
                      toggleLanguage();
                      setShowLanguageDropdown(false);
                    }}
                    className={`w-8 h-8 rounded-full overflow-hidden transition-transform duration-300 hover:scale-110 ${language === 'pt' ? 'border-2 border-[var(--color-primary)]' : 'border border-gray-300'}`}
                    title="Português"
                  >
                    <img src={ptFlagSvg} alt="Português" className="w-full h-full object-cover" />
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Drawer Navigation for Mobile */}
      <DrawerNuclear
        isOpen={mobileMenu}
        onClose={() => setMobileMenu(false)}
        logo={restaurantConfig.logoImage}
        language={language}
        toggleLanguage={toggleLanguage}
        handleReservation={scrollToBookingSection}
      />
    </header>
  );
}; 